/**
 * ExecutionContext — Runtime state for interpreted graph execution.
 *
 * Holds variable state, port output cache, and provides the engine bridge
 * that nodes use during interpreted (non-compiled) execution.
 */

import type { ExecutionState, NodeExecuteContext, PortValue, PortDataType } from '../types';
import { defaultValueFor } from '../node/PortTypes';

// ============================================================================
// Engine bridge interface (injected by editor)
// ============================================================================

/**
 * Abstraction over engine API calls that nodes can invoke at runtime.
 * The editor/game provides a concrete implementation.
 */
export interface EngineBridge {
  // Time
  getTime(): number;
  getDeltaTime(): number;

  // Transform
  getPositionX(entityId: number): number;
  getPositionY(entityId: number): number;
  setPosition(entityId: number, x: number, y: number): void;
  getRotation(entityId: number): number;
  setRotation(entityId: number, angle: number): void;
  getScaleX(entityId: number): number;
  getScaleY(entityId: number): number;
  setScale(entityId: number, x: number, y: number): void;

  // Sprite
  setSpriteColor(entityId: number, color: number): void;
  setSpriteAlpha(entityId: number, alpha: number): void;

  // Input
  isKeyDown(key: string): boolean;
  isKeyPressed(key: string): boolean;

  // Debug
  log(message: string): void;

  // Generic method dispatch (for extensibility)
  call(method: string, ...args: unknown[]): unknown;
}

/**
 * No-op bridge for testing / standalone execution.
 */
export class NullBridge implements EngineBridge {
  getTime(): number { return 0; }
  getDeltaTime(): number { return 0.016; }
  getPositionX(_entityId: number): number { return 0; }
  getPositionY(_entityId: number): number { return 0; }
  setPosition(_entityId: number, _x: number, _y: number): void {}
  getRotation(_entityId: number): number { return 0; }
  setRotation(_entityId: number, _angle: number): void {}
  getScaleX(_entityId: number): number { return 1; }
  getScaleY(_entityId: number): number { return 1; }
  setScale(_entityId: number, _x: number, _y: number): void {}
  setSpriteColor(_entityId: number, _color: number): void {}
  setSpriteAlpha(_entityId: number, _alpha: number): void {}
  isKeyDown(_key: string): boolean { return false; }
  isKeyPressed(_key: string): boolean { return false; }
  log(message: string): void { console.log(`[Redprint]`, message); }
  call(method: string, ..._args: unknown[]): unknown {
    console.warn(`[NullBridge] call('${method}') not implemented`);
    return undefined;
  }
}

// ============================================================================
// ExecutionContext
// ============================================================================

export class ExecutionContext {
  /** Variable storage */
  private _variables = new Map<string, PortValue>();

  /** Port output cache: "nodeId:portId" → value */
  private _portCache = new Map<string, PortValue>();

  /** Per-node persistent state: "nodeId:key" → value (survives across frames) */
  private _nodeState = new Map<string, unknown>();

  /** Entity this graph is attached to */
  private _entityId = -1;

  /** Current delta time (set by executor per frame) */
  deltaTime = 0;

  /** Current time (set by executor per frame) */
  time = 0;

  constructor(
    readonly bridge: EngineBridge = new NullBridge(),
  ) {}

  // --------------------------------------------------------------------------
  // Entity
  // --------------------------------------------------------------------------

  get entityId(): number { return this._entityId; }
  set entityId(id: number) { this._entityId = id; }

  // --------------------------------------------------------------------------
  // Variables
  // --------------------------------------------------------------------------

  /**
   * Initialize variables from graph defaults.
   */
  initVariables(variables: readonly { name: string; type: PortDataType; defaultValue: unknown }[]): void {
    this._variables.clear();
    for (const v of variables) {
      this._variables.set(v.name, (v.defaultValue ?? defaultValueFor(v.type)) as PortValue);
    }
  }

  getVariable<T = PortValue>(name: string): T {
    return (this._variables.get(name) ?? null) as T;
  }

  setVariable(name: string, value: PortValue): void {
    this._variables.set(name, value);
  }

  // --------------------------------------------------------------------------
  // Port cache
  // --------------------------------------------------------------------------

  getPortOutput(nodeId: string, portId: string): PortValue | undefined {
    return this._portCache.get(`${nodeId}:${portId}`);
  }

  setPortOutput(nodeId: string, portId: string, value: PortValue): void {
    this._portCache.set(`${nodeId}:${portId}`, value);
  }

  /**
   * Clear port cache (call at start of each execution pass).
   */
  clearPortCache(): void {
    this._portCache.clear();
  }

  // --------------------------------------------------------------------------
  // Per-node state (persistent across frames)
  // --------------------------------------------------------------------------

  getNodeState<T = unknown>(nodeId: string, key: string): T | undefined {
    return this._nodeState.get(`${nodeId}:${key}`) as T | undefined;
  }

  setNodeState(nodeId: string, key: string, value: unknown): void {
    this._nodeState.set(`${nodeId}:${key}`, value);
  }

  /**
   * Clear all per-node state (call on graph reset).
   */
  clearNodeState(): void {
    this._nodeState.clear();
  }

  // --------------------------------------------------------------------------
  // Snapshot
  // --------------------------------------------------------------------------

  getState(): ExecutionState {
    return {
      variables: new Map(this._variables),
      portCache: new Map(this._portCache),
    };
  }

  // --------------------------------------------------------------------------
  // Create NodeExecuteContext for a specific node
  // --------------------------------------------------------------------------

  /**
   * Build a `NodeExecuteContext` for interpreting a specific node.
   * The `getInput` and `setOutput` closures are bound to this context.
   */
  createNodeContext(
    nodeId: string,
    inputResolver: (portId: string) => PortValue,
    flowTrigger: (portId: string) => void,
    triggeredPort?: string,
  ): NodeExecuteContext {
    return {
      getInput: <T = PortValue>(portId: string): T => {
        return inputResolver(portId) as T;
      },

      setOutput: (portId: string, value: PortValue): void => {
        this.setPortOutput(nodeId, portId, value);
      },

      getVariable: <T = PortValue>(name: string): T => {
        return this.getVariable<T>(name);
      },

      setVariable: (name: string, value: PortValue): void => {
        this.setVariable(name, value);
      },

      triggerFlow: (portId: string): void => {
        flowTrigger(portId);
      },

      getState: <T = unknown>(key: string): T | undefined => {
        return this.getNodeState<T>(nodeId, key);
      },

      setState: (key: string, value: unknown): void => {
        this.setNodeState(nodeId, key, value);
      },

      triggeredPort,

      bridge: this.bridge,
      entityId: this._entityId,
      deltaTime: this.deltaTime,
      time: this.time,
    };
  }
}
